import React, { useState, useRef } from 'react';
import styles from './ShipDataManager.module.css';
import { useAuth } from '../context/AuthContext';

interface ImportResult {
  message: string;
  shipsCreated: number;
  shipsUpdated: number;
  hardpointsCreated: number;
  totalShips: number;
}

const ShipDataManager: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');

  // Check if user is admin
  const isAdmin = user?.role === 'Admin' || user?.role === 'Leadership';

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You must be an Admin or Leadership member to access this page.</p>
        </div>
      </div>
    );
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        setError('Please select a JSON file');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setImportResult(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        setError('Please select a JSON file');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setImportResult(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const importShipData = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportResult(null);
    setProgress('Reading file...');

    try {
      const fileContent = await selectedFile.text();
      const jsonData = JSON.parse(fileContent);

      // Validate the JSON structure
      if (!jsonData.ships || !Array.isArray(jsonData.ships)) {
        throw new Error('Invalid file format. Expected { ships: [...] }');
      }

      const totalShips = jsonData.ships.length;
      const batchSize = 20;
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalHardpoints = 0;

      // Import in batches to avoid timeout
      for (let i = 0; i < totalShips; i += batchSize) {
        const batch = jsonData.ships.slice(i, Math.min(i + batchSize, totalShips));
        const batchNum = Math.floor(i / batchSize) + 1;
        const totalBatches = Math.ceil(totalShips / batchSize);
        
        setProgress(`Importing batch ${batchNum}/${totalBatches} (ships ${i + 1}-${Math.min(i + batchSize, totalShips)})...`);

        const apiUrl = process.env.REACT_APP_API_URL || 'https://api-staging.tficorg.org';
        const response = await fetch(`${apiUrl}/api/ships/import-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ships: batch }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Batch ${batchNum} failed: ${response.statusText}`);
        }

        const result = await response.json();
        totalCreated += result.shipsCreated || 0;
        totalUpdated += result.shipsUpdated || 0;
        totalHardpoints += result.hardpointsCreated || 0;
      }

      setImportResult({
        message: 'Import completed successfully!',
        shipsCreated: totalCreated,
        shipsUpdated: totalUpdated,
        hardpointsCreated: totalHardpoints,
        totalShips: totalShips,
      });
      setProgress('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setProgress('');
    } finally {
      setIsImporting(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setError(null);
    setImportResult(null);
    setProgress('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Ship Data Manager</h1>
        <p className={styles.subtitle}>Import P4K ship data to update the database</p>
      </div>

      <div className={styles.instructions}>
        <h3>📋 Instructions</h3>
        <ol>
          <li>Run the P4K extraction script locally after a game update</li>
          <li>Start your local API server and visit <code>http://localhost:5000/api/ships/export</code></li>
          <li>Save the JSON response to a file</li>
          <li>Upload the JSON file below and click "Import"</li>
        </ol>
        <div className={styles.scriptHint}>
          <strong>Quick Export Command (PowerShell):</strong>
          <code>Invoke-WebRequest "http://localhost:5000/api/ships/export" -OutFile "ship-export.json"</code>
        </div>
      </div>

      <div className={styles.uploadSection}>
        <div
          className={`${styles.dropZone} ${selectedFile ? styles.hasFile : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className={styles.fileInput}
          />
          {selectedFile ? (
            <div className={styles.fileInfo}>
              <span className={styles.fileIcon}>📄</span>
              <span className={styles.fileName}>{selectedFile.name}</span>
              <span className={styles.fileSize}>({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            </div>
          ) : (
            <div className={styles.dropPrompt}>
              <span className={styles.uploadIcon}>📤</span>
              <p>Drag & drop your ship export JSON here</p>
              <p className={styles.orText}>or click to browse</p>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.importButton}
            onClick={importShipData}
            disabled={!selectedFile || isImporting}
          >
            {isImporting ? (
              <>
                <span className={styles.spinner}></span>
                Importing...
              </>
            ) : (
              <>
                <span>🚀</span>
                Import Ship Data
              </>
            )}
          </button>
          {selectedFile && !isImporting && (
            <button className={styles.clearButton} onClick={clearSelection}>
              Clear
            </button>
          )}
        </div>

        {progress && (
          <div className={styles.progress}>
            <span className={styles.progressSpinner}></span>
            {progress}
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <span>❌</span>
            {error}
          </div>
        )}

        {importResult && (
          <div className={styles.success}>
            <h3>✅ {importResult.message}</h3>
            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.statValue}>{importResult.totalShips}</span>
                <span className={styles.statLabel}>Total Ships</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>{importResult.shipsCreated}</span>
                <span className={styles.statLabel}>Created</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>{importResult.shipsUpdated}</span>
                <span className={styles.statLabel}>Updated</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>{importResult.hardpointsCreated}</span>
                <span className={styles.statLabel}>Hardpoints</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipDataManager;
